import { useCallback, useState } from "react";
import { useModal } from "@ludens-reklame/react-modal";
import { Product as IProduct } from "@ludens-reklame/rubics-v3-sdk/product/types";
import { transformImageUrl } from "@ludens-reklame/rubics-v3-sdk/file/utils";
import { useStore } from "@ludens-reklame/rubics-v3-react/store/hooks";
import { useCart } from "@ludens-reklame/rubics-v3-react/cart/hooks";
import { Theme } from "../../types/theme.js";
import { StoreBuckets } from "../../types/dataBuckets.js";
import {
  AddToCart,
  StyledExpandableSection,
  Gallery,
  Layout,
  Section,
  ExpandableSections,
} from "./styles.js";
import { RichText } from "../../internal-components/RichText.js";
import {
  ExpandableSection as IExpandableSection,
  ProductProps,
} from "../../types/product.js";
import { ProductPrice } from "../../internal-components/product/ProductPrice.js";
import { Button } from "../../internal-components/Button.js";
import { Attribute } from "../../internal-components/product/Attribute.js";
import { ImageGallery } from "../../internal-components/product/ImageGallery.js";
import {
  Constrainer,
  HorizontalSpacer,
  VerticalSpacer,
} from "../../internal-components/Layout.js";
import { Counter } from "../../internal-components/Counter.js";
import { Ruler } from "../../internal-components/product/Ruler.js";
import { ProductLabelImage } from "../../internal-components/product/ProductLabel.js";
import { AddToCartModal } from "./AddToCartModal.js";
import { minus, plus } from "../../icons/index.js";

export const Product: React.FC = () => {
  const [quantity, setQuantity] = useState<number>(1);
  const store = useStore<Theme, StoreBuckets, {}, ProductProps>();
  const { spawnModal } = useModal();
  const cart = useCart();
  const product = store.context.product;

  const addToCart = useCallback(async () => {
    if (!product) {
      return;
    }

    const item = await cart.addItem({ product: product._id, quantity });
    setQuantity(1);

    if (item) {
      spawnModal(<AddToCartModal item={item} />);
    }
  }, [product, quantity, spawnModal]);

  if (!product) {
    return null;
  }

  const notPurchasable =
    cart.networking || product.stockStatus === "outOfStock";

  return (
    <VerticalSpacer>
      <HorizontalSpacer>
        <Constrainer>
          <Layout>
            <Gallery>
              {product.labels.map((label) => {
                if (label.image) {
                  return (
                    <ProductLabelImage
                      $larger
                      src={transformImageUrl(label.image, {
                        width: 240,
                        height: 240,
                        fit: "contain",
                      })}
                      alt={label.image.altText}
                    />
                  );
                }

                return null;
              })}
              <ImageGallery images={product.images} />
            </Gallery>
            <div>
              <h1 className="h2">{product.name}</h1>
              <Ruler />
              <p>
                <ProductPrice product={product} priceClass="h4" />
              </p>
              {product.attributes.map((a) => (
                <Section key={a._id}>
                  <Attribute attribute={a} product={product} />
                </Section>
              ))}
              <Section $large>
                <AddToCart>
                  <Counter
                    value={quantity}
                    onChange={setQuantity}
                    disabled={notPurchasable}
                  />
                  <Button disabled={notPurchasable} onClick={addToCart}>
                    {cart.networking ? "Laster…" : renderAddToCartText(product)}
                  </Button>
                </AddToCart>
              </Section>
              {product.extendedDescription && (
                <Section $large>
                  <RichText
                    dangerouslySetInnerHTML={{
                      __html: product.extendedDescription,
                    }}
                  />
                </Section>
              )}
              {product.props.expandableSections &&
                product.props.expandableSections.length > 0 && (
                  <ExpandableSections>
                    {(product.props.expandableSections || []).map((s) => (
                      <ExpandableSection key={s.title} section={s} />
                    ))}
                  </ExpandableSections>
                )}
            </div>
          </Layout>
        </Constrainer>
      </HorizontalSpacer>
    </VerticalSpacer>
  );
};

const ExpandableSection: React.FC<{
  section: IExpandableSection;
  initiallyExpanded?: boolean;
}> = ({ section, initiallyExpanded }) => {
  const [expanded, setExpanded] = useState<boolean>(initiallyExpanded || false);
  return (
    <StyledExpandableSection>
      <h2 className="h4">
        <a
          href="#"
          aria-label={expanded ? "Minimer" : "Ekspander"}
          onClick={(e) => {
            e.preventDefault();
            setExpanded(!expanded);
          }}
        >
          {section.title} {expanded ? minus : plus}
        </a>
      </h2>
      {expanded && section.content && (
        <RichText dangerouslySetInnerHTML={{ __html: section.content }} />
      )}
    </StyledExpandableSection>
  );
};

function renderAddToCartText(product: IProduct<ProductProps>): string {
  if (product.stockStatus === "outOfStock") {
    return "Ikke på lager";
  }

  return product.props.addToCartText || "Legg i handlekurv";
}
